<template>
  <div class="map-container">
    <div class="map" id="tdtMap"></div>
    <!-- 天地图版权信息 -->
    <div class="tianditu-copyright">
      <img class="tianditu-logo" src="/image/logo.png" alt="天地图" />
      <span class="tianditu-license">GS (2025) 1508号 - 甲测资字11110974</span>
    </div>
  </div>
</template>

<script>
import 'ol/ol.css'
import { Map, View, Feature } from 'ol'
import TileLayer from 'ol/layer/Tile'
import XYZ from 'ol/source/XYZ'
import { defaults as defaultControls } from 'ol/control'
import Point from 'ol/geom/Point'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Style from 'ol/style/Style'
import Icon from 'ol/style/Icon'
import Text from 'ol/style/Text'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import { mapConfig } from '../config/mapConfig'

export default {
  name: 'TianDiTuMap',
  props: {
    // 中心点坐标，格式为 [经度, 纬度]
    center: {
      type: Array,
      default: () => [0.0, 0.0]
    },
    // 缩放级别
    zoom: {
      type: Number,
      default: 14
    },
    // 动画持续时间（毫秒）
    duration: {
      type: Number,
      default: 1000
    },
    // 企业位置标记图片路径
    companyMarkerImageSrc: {
      type: String,
      default: '/image/kq_position_company.png'
    },
    // 企业位置文字标签
    tipsText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      map: null,
      markerLayer: null,
      isMobile: false
    }
  },
  mounted() {
    // 判断是否为移动设备
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log('地图组件初始化，中心点:', this.center, '缩放级别:', this.zoom);
    
    // 先设置地图容器的最小高度，确保它在初始化时可见
    const mapContainer = document.getElementById('tdtMap');
    if (mapContainer) {
      mapContainer.style.minHeight = '400px';
      console.log('容器尺寸:', mapContainer.getBoundingClientRect());
    }
    
    // 初始化地图
    setTimeout(() => {
      this.initMap();
      // 确保地图容器可见后更新尺寸
      this.$nextTick(() => {
        if (this.map) {
          this.map.updateSize();
          console.log('地图尺寸已更新');
        }
      });
    }, 100);
    
    // 监听窗口大小变化，调整地图大小
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    if (this.map) {
      this.map.dispose()
      window.removeEventListener('resize', this.handleResize)
    }
  },
  watch: {
    // 监听中心点变化，更新地图位置
    center: {
      handler(newCenter) {
        if (this.map && newCenter && newCenter.length === 2) {
          this.setCenter(newCenter, this.zoom)
          // 更新标记位置
          this.$nextTick(() => {
            this.updateMarkers();
          });
        }
      },
      deep: true
    },
    // 监听缩放级别变化，更新地图缩放
    zoom(newZoom) {
      if (this.map) {
        this.setZoom(newZoom)
      }
    },
    // 监听标记图片变化
    companyMarkerImageSrc() {
      if (this.map) {
        this.$nextTick(() => {
          this.updateMarkers();
        });
      }
    },
    // 监听提示文字变化
    tipsText() {
      if (this.map) {
        this.$nextTick(() => {
          this.updateMarkers();
        });
      }
    }
  },
  methods: {
    initMap() {
      try {
        // 确保有有效的中心点坐标
        const mapCenter = this.center && this.center.length === 2 ? this.center : [104.066541, 30.659462];
        // 直接使用传入的zoom值，移动设备使用更高的缩放级别
        const zoom = this.zoom ?? 16;
        
        console.log('开始初始化地图，使用中心点:', mapCenter, '缩放:', zoom);
        
        // 创建地图实例
        const map = new Map({
          target: 'tdtMap',
          view: new View({
            center: mapCenter,
            zoom: zoom,
            projection: 'EPSG:4326',
            // 增加一些动画效果
            enableRotation: false
          }),
          controls: defaultControls({
            zoom: false,
            attribution: false,
            rotate: false
          })
        })
        
        console.log('地图实例创建成功');
        this.map = map
        
        // 为移动设备优化的额外设置
        if (this.isMobile) {
          map.getInteractions().forEach(interaction => {
            if (interaction && interaction.constructor && interaction.constructor.name === 'DoubleClickZoom') {
              map.removeInteraction(interaction);
            }
          });
        }
        
        // 添加天地图底图
        console.log('添加天地图底图...');
        let url = 'http://t{0-7}.tianditu.com/DataServer?x={x}&y={y}&l={z}'
        url = `${url}&T=vec_c&tk=${mapConfig.tianDiTuKey}`
        const source = new XYZ({
          url: url,
          projection: 'EPSG:4326',
          crossOrigin: 'anonymous',
          tileLoadFunction: XYZ.defaultTileLoadFunction
        })
        const tdtLayer = new TileLayer({
          source: source
        })
        map.addLayer(tdtLayer)
        console.log('天地图底图添加成功');
        
        // 添加天地图注记层
        let cvaUrl = 'http://t{0-7}.tianditu.com/DataServer?x={x}&y={y}&l={z}'
        cvaUrl = `${cvaUrl}&T=cva_c&tk=${mapConfig.tianDiTuKey}`
        const sourceCVA = new XYZ({
          url: cvaUrl,
          projection: 'EPSG:4326',
          crossOrigin: 'anonymous',
          tileLoadFunction: XYZ.defaultTileLoadFunction
        })
        const tdtcvaLayer = new TileLayer({
          source: sourceCVA
        })
        map.addLayer(tdtcvaLayer)
        
        // 更新标记
        this.updateMarkers();
        
        // 记录使用的参数到控制台，方便调试
        console.log('地图初始化参数:', {
          center: { lng: mapCenter[0], lat: mapCenter[1] },
          zoom: zoom,
          tipsText: this.tipsText
        });
        
        console.log('地图初始化完成');
      } catch (error) {
        console.error('地图初始化失败:', error);
        // 添加错误提示给用户
        const mapContainer = document.getElementById('tdtMap');
        if (mapContainer) {
          mapContainer.innerHTML = '<div style="display: flex; justify-content: center; align-items: center; height: 100%; color: #ff4d4f; font-size: 14px;">地图加载失败，请刷新页面重试</div>';
        }
      }
    },
    
    /**
     * 更新标记
     */
    updateMarkers() {
      // 移除旧图层
      if (this.markerLayer) {
        this.map.removeLayer(this.markerLayer);
      }

      // 根据设备类型调整图标大小
      const iconScale =  0.55;
      
      // 如果中心点坐标有效，则添加企业位置标记
      if (this.center && this.center.length === 2 && this.center[0] !== 0.0 && this.center[1] !== 0.0) {
        // 创建企业位置标记
        const companyPoint = new Point(this.center);
        const companyFeature = new Feature({ geometry: companyPoint });
        
        // 设置企业位置标记样式
        const companyIconStyle = new Style({
          image: new Icon({
            src: this.companyMarkerImageSrc,
            anchor: [0.5, 1],
            scale: iconScale
          })
        });
        
        // 创建文字标签样式
        const textStyle = new Style({
          text: new Text({
            text: this.tipsText,
            font: '13px sans-serif',
              fill: new Fill({ color: '#000000' }), // 文字颜色为白色
              stroke: new Stroke({ color: '#ffffff', width: 4 }), // 蓝色边框
              padding: [5, 10, 5, 10], // 内边距
              backgroundFill: new Fill({ color: '#ffffff' }), // 蓝色背景
              backgroundStroke: new Stroke({
                color: '#ffffff',
                width: 1
              }),
              backgroundRadius: [8, 8, 8, 8], // 圆角
              offsetY: -60,
              textAlign: 'center' // 文字居中对齐
          })
        });
        
        companyFeature.setStyle([companyIconStyle, textStyle]);
        
        // 创建企业标记图层
        this.markerLayer = new VectorLayer({
          source: new VectorSource({
            features: [companyFeature]
          }),
          zIndex: 2
        });
        
        this.map.addLayer(this.markerLayer);
      }
    },
    
    /**
     * 设置地图中心点
     * @param {Array} center 中心点坐标 [经度, 纬度]
     * @param {Number} zoom 可选的缩放级别
     * @param {Number} duration 可选的动画持续时间
     */
    setCenter(center, zoom = null, duration = this.duration) {
      if (!this.map || !center || center.length !== 2) return
      
      const view = this.map.getView()
      const options = { duration }
      
      if (zoom !== null) {
        view.animate({
          center,
          zoom,
          ...options
        })
      } else {
        view.animate({
          center,
          ...options
        })
      }
    },
    
    /**
     * 设置地图缩放级别
     * @param {Number} zoom 缩放级别
     * @param {Number} duration 可选的动画持续时间
     */
    setZoom(zoom, duration = this.duration) {
      if (!this.map || typeof zoom !== 'number') return
      
      this.map.getView().animate({
        zoom,
        duration
      })
    },
    
    /**
     * 获取当前地图中心点
     * @returns {Array} 当前中心点坐标 [经度, 纬度]
     */
    getCurrentCenter() {
      if (!this.map) return this.center
      return this.map.getView().getCenter()
    },
    
    /**
     * 获取当前地图缩放级别
     * @returns {Number} 当前缩放级别
     */
    getCurrentZoom() {
      if (!this.map) return this.zoom
      return this.map.getView().getZoom()
    },
    
    /**
     * 处理窗口大小变化
     */
    handleResize() {
      if (this.map) {
        this.map.updateSize()
      }
    }
  }
}
</script>

<style scoped>
.map-container {
  width: 100%;
  height: 100vh;
  position: relative;
  overflow: hidden;
}

#tdtMap {
    width: 100%;
    height: 100%;
    min-height: 400px;
  }

/* 天地图版权信息样式 */
.tianditu-copyright {
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 100;
  display: flex;
  align-items: center;
  /* background-color: rgba(255, 255, 255, 0.8); */
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); */
}

.tianditu-logo {
   width: auto;
   height: 20px;
   margin-right: 6px;
   padding-right: 6px;
   /* border-right: 1px solid #ddd; */
}

.tianditu-license {
  color: #666;
}

  /* 确保在各种屏幕尺寸下都能正确显示 */
  @media (max-width: 768px) {
    .map-container {
      height: 100vh;
      min-height: 500px;
    }
  }
</style>